import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Persistent "Switch Demo Profile" control for the Master Platform Demo.
 * Only renders for the four synthetic showcase accounts (or a presenter),
 * so real users never see it. No logout required to switch lives.
 */
export default function DemoProfileSwitcher() {
  const { user, token } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');

  const eligible = !!(user && (user.showcase_profile || user.showcase_presenter || user.showcase_viewer));

  useEffect(() => {
    if (!eligible || !token) return;
    axios.get(`${API}/labs/showcase`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setProfiles(r.data.profiles || []))
      .catch(() => setProfiles([]));
  }, [eligible, token]);

  if (!eligible || profiles.length === 0) return null;

  const switchTo = async (key) => {
    setBusy(key);
    try {
      const res = await axios.post(`${API}/labs/showcase/enter`, { key },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      localStorage.setItem('futurepath_token', res.data.access_token);
      window.location.href = '/app/dashboard';
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-50" data-testid="demo-profile-switcher">
      {open ? (
        <div className="w-72 rounded-2xl border border-cyan-400/30 bg-slate-900/95 backdrop-blur p-3 shadow-2xl">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider text-cyan-300">Switch demo profile</p>
            <button onClick={() => setOpen(false)} data-testid="switcher-close"><X className="w-4 h-4 text-slate-300" /></button>
          </div>
          <div className="space-y-1.5">
            {profiles.map(p => (
              <button key={p.key} onClick={() => switchTo(p.key)} disabled={!!busy}
                data-testid={`switch-to-${p.key}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                  p.active ? 'bg-cyan-500/15 text-white border border-cyan-400/40' : 'bg-slate-800/70 text-slate-200 hover:bg-slate-700'
                }`}>
                <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${p.avatar_gradient} flex items-center justify-center text-[10px] font-bold text-white shrink-0`}>
                  {p.name.split(' ').map(n => n[0]).join('')}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{p.name.split(' ')[0]}</span>
                  <span className="block text-[10px] text-slate-300 truncate">{p.archetype_label}</span>
                </span>
                {busy === p.key && <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300" />}
              </button>
            ))}
          </div>
          <a href="/AFPLabs/showcase" className="block text-center text-[11px] text-slate-300 hover:text-white mt-2">Back to the showcase</a>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="switcher-open"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900/95 border border-cyan-400/40 text-sm text-white shadow-xl backdrop-blur">
          <Users className="w-4 h-4 text-cyan-300" />
          Demo: {(user.full_name || '').split(' ')[0] || 'Presenter'}
        </button>
      )}
    </div>
  );
}
